import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, SignOut, CreditCard, ShieldStar, ArrowSquareOut, X, Bell, Star } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/lib/useConfig";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const STATUS_CONFIG = {
  not_started: { label: "Not started", badge: "bg-slate-100 text-slate-600 border-slate-300", dot: "bg-slate-400" },
  in_progress: { label: "In progress", badge: "bg-blue-100 text-blue-700 border-blue-200", dot: "bg-blue-500" },
  live: { label: "Live", badge: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  rejected: { label: "Rejected", badge: "bg-red-100 text-red-700 border-red-200", dot: "bg-red-500" },
};

export const StatusBadge = ({ status }) => {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.not_started;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${c.badge}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
};

export default function Layout({ children }) {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [cancelOpen, setCancelOpen] = useState(false);
  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  const cancelMembership = async () => {
    try {
      await api.post("/subscription/cancel");
      toast.success("Membership canceled");
      setCancelOpen(false);
      await checkAuth();
      navigate("/subscribe", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} data-testid="header-logo" className="flex items-center group">
            <img src="/citehub-logo.png" alt="Cite-Hub" className="h-9 w-auto transition-transform group-hover:-translate-y-0.5" />
          </button>

          <div className="flex items-center gap-1.5">
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-button" className="flex items-center gap-2.5 rounded-full pl-1 pr-3 py-1 hover:bg-slate-50 transition-colors">
                <Avatar className="h-8 w-8">
                  {user?.picture ? <AvatarImage src={user.picture} alt="" /> : null}
                  <AvatarFallback className="bg-slate-900 text-white text-xs font-bold">{initials}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-semibold text-slate-700 hidden sm:block max-w-[160px] truncate">{user?.name || user?.email}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {user?.role === "admin" ? (
                <DropdownMenuItem data-testid="admin-menu-link" onClick={() => navigate("/admin")} className="cursor-pointer">
                  <ShieldStar size={16} className="mr-2" /> Members (Admin)
                </DropdownMenuItem>
              ) : (
                <>
                  <div className="px-2 py-1.5 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-600">
                      <CreditCard size={14} /> {user?.comped ? "Free access" : (user?.subscription_plan === "auto_sync" ? "Auto-Sync plan" : "Self-Serve plan")}
                    </span>
                    {!user?.comped && <p className="mt-1 text-slate-400">Renews at ${user?.subscription_plan === "auto_sync" ? "49.00" : "7.99"}/month</p>}
                  </div>
                  {user?.has_stripe_subscription && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid="cancel-membership-button" onClick={() => setCancelOpen(true)} className="cursor-pointer">
                        <CreditCard size={16} className="mr-2" /> Cancel membership
                      </DropdownMenuItem>
                    </>
                  )}
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-red-600 focus:text-red-700 cursor-pointer">
                <SignOut size={16} className="mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>
      <PromoBanner />
      <main>{children}</main>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="cancel-membership-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel your membership?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll lose access to your businesses and citation tracking until you reactivate. This cancels your Stripe subscription immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-membership-dismiss">Keep membership</AlertDialogCancel>
            <AlertDialogAction onClick={cancelMembership} data-testid="cancel-membership-confirm" className="bg-red-600 hover:bg-red-700">
              Cancel membership
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


export const PromoBanner = () => {
  const config = useConfig();
  const [hidden, setHidden] = useState(() => localStorage.getItem("citehub_promo_hidden") === "1");
  if (hidden || !config?.company_url) return null;
  const dismiss = () => { localStorage.setItem("citehub_promo_hidden", "1"); setHidden(true); };
  return (
    <div data-testid="promo-banner" className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-11 flex items-center justify-center gap-3 text-sm relative">
        <a href={config.company_url} target="_blank" rel="noreferrer" data-testid="promo-banner-link"
          className="flex items-center gap-2 font-medium hover:text-blue-300 transition-colors">
          <span className="hidden sm:inline text-slate-400">Brought to you by</span>
          <span className="font-bold">{config.company_name}</span>
          <span className="text-slate-400">— visit our site</span>
          <ArrowSquareOut size={15} weight="bold" />
        </a>
        <button onClick={dismiss} data-testid="promo-banner-dismiss"
          className="absolute right-4 text-slate-400 hover:text-white transition-colors"><X size={16} /></button>
      </div>
    </div>
  );
};


export const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); setUnread(0); setItems((p) => p.map((n) => ({ ...n, read: true }))); } catch { /* ignore */ }
  };

  const openItem = async (n) => {
    setOpen(false);
    try { await api.post(`/notifications/${n.notification_id}/read`); } catch { /* ignore */ }
    load();
    if (n.business_id) navigate(`/business/${n.business_id}/reviews`);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); }}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative h-9 w-9 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors">
          <Bell size={20} weight={unread > 0 ? "fill" : "regular"} className={unread > 0 ? "text-[#0055FF]" : "text-slate-500"} />
          {unread > 0 && (
            <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <span className="font-bold text-slate-900 text-sm">Alerts</span>
          {unread > 0 && <button onClick={markAll} data-testid="mark-all-read" className="text-xs font-semibold text-[#0055FF] hover:underline">Mark all read</button>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">No alerts yet.</div>
          ) : items.map((n) => (
            <button key={n.notification_id} onClick={() => openItem(n)} data-testid={`notification-${n.notification_id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors flex gap-3 ${n.read ? "" : "bg-blue-50/40"}`}>
              <span className="mt-0.5 h-8 w-8 shrink-0 rounded-full bg-amber-100 flex items-center justify-center">
                <Star weight="fill" size={15} className="text-amber-500" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-900">{n.title}</span>
                <span className="block text-xs text-slate-500 truncate">{n.message}</span>
              </span>
              {!n.read && <span className="ml-auto mt-1 h-2 w-2 rounded-full bg-[#0055FF] shrink-0" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
